/**
 * Время записи «Недавно добавленных» (ТЗ главной § 22.4) — одно правило для всех мест, по часам
 * устройства, от `createdAt`. Чистые функции без alias-импортов и без i18n: возвращают вид подписи,
 * тексты ставит виджет (`generations.time.*`), даты пишет Intl.DateTimeFormat языка интерфейса.
 */

export type GenerationTime =
  /** Меньше минуты (и время из будущего — часы устройства спешат). */
  | { kind: 'now' }
  /** Меньше часа — «5 мин», и через полночь тоже. */
  | { kind: 'min'; count: number }
  /** В этот календарный день — «3 ч». */
  | { kind: 'hour'; count: number }
  /** В предыдущий календарный день. */
  | { kind: 'yesterday' }
  /** Раньше: «12 окт.» в этом году, «12.10.2025» — в другом. */
  | { kind: 'date'; text: string };

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

const dayStart = (d: Date, shiftDays = 0) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + shiftDays).getTime();

export function formatGenerationTime(iso: string, now: number, lang: string): GenerationTime {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return { kind: 'now' };
  const diff = now - at;
  if (diff < MINUTE) return { kind: 'now' };
  if (diff < HOUR) return { kind: 'min', count: Math.floor(diff / MINUTE) };
  const date = new Date(at);
  const today = new Date(now);
  const day = dayStart(date);
  if (day === dayStart(today)) return { kind: 'hour', count: Math.floor(diff / HOUR) };
  if (day === dayStart(today, -1)) return { kind: 'yesterday' };
  // Не «12 окт. 2025 г.»: на 320 px это отняло бы у заголовка ещё 22 px.
  const options: Intl.DateTimeFormatOptions =
    date.getFullYear() === today.getFullYear()
      ? { day: 'numeric', month: 'short' }
      : { day: '2-digit', month: '2-digit', year: 'numeric' };
  return { kind: 'date', text: new Intl.DateTimeFormat(lang, options).format(date) };
}

/** Полные дата и время для `aria-label` заголовка записи: «20 октября, 12:41». */
export function formatGenerationFullDate(iso: string, now: number, lang: string): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return '';
  const date = new Date(at);
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  const day = new Intl.DateTimeFormat(lang, sameYear ? { day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
  const time = new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit' }).format(date);
  return `${day}, ${time}`;
}
